import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { RenewalsService } from '@app/services/renewals.service';
import { RestapiService } from '@app/services/restapi.service';
import { selectSelectedPortfolioId } from '@dashboards/store/selectors/property-selector.selectors';
import { RestThresholdSection } from '@shared/interfaces/rest-threshold-section';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import {
  getRenewalTargetList,
  getRenewalTargetListFailure,
  getRenewalTargetListSuccess,
  loadAllThresholds,
  loadAllThresholdsFailed,
  loadAllThresholdsSuccess,
  loadThresholdsProperty,
  loadThresholdsPropertyFailed,
  loadThresholdsPropertySuccess,
  loadThresholdsTask,
  loadThresholdsTaskFailed,
  loadThresholdsTaskSuccess,
  setThresholdsConfig,
  setThresholdsConfigFailed,
  setThresholdsConfigSuccess,
  updateRenewalTarget,
  updateRenewalTargetFailure,
  updateRenewalTargetSuccess,
} from './management.actions';

@Injectable()
export class ManagementEffects {
  constructor(
    private actions$: Actions,
    private restApiService: RestapiService,
    private store: Store<{}>,
    private snackbarService: SnackbarService,
    private renewalsService: RenewalsService
  ) {}

  loadAllThresholds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllThresholds),
      exhaustMap(() =>
        this.restApiService.getData<RestThresholdSection[]>(`Thresholds`).pipe(
          map(thresholdSections => loadAllThresholdsSuccess({ thresholdSections })),
          catchError(err => of(loadAllThresholdsFailed()))
        )
      )
    )
  );

  loadThresholdsProperty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadThresholdsProperty),
      switchMap(action =>
        this.restApiService.getData<RestThresholdSection[]>(`Thresholds/portfolio/${action.portfolioId}`).pipe(
          map(thresholdProperty => loadThresholdsPropertySuccess({ thresholdProperty })),
          catchError(err => of(loadThresholdsPropertyFailed()))
        )
      )
    )
  );

  loadThresholdsTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadThresholdsTask),
      exhaustMap(() =>
        this.restApiService.getData<RestThresholdSection[]>(`Thresholds/tasks`).pipe(
          map(thresholdTask => loadThresholdsTaskSuccess({ thresholdTask })),
          catchError(err => of(loadThresholdsTaskFailed()))
        )
      )
    )
  );

  setThresholdsConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setThresholdsConfig),
      switchMap(action =>
        this.restApiService.create<boolean>(`Thresholds`, action.threshold).pipe(
          withLatestFrom(this.store.select(selectSelectedPortfolioId)),
          mergeMap(([success, portfolioId]) => [
            loadThresholdsProperty({ portfolioId }),
            loadThresholdsTask(),
            setThresholdsConfigSuccess(),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.ExecutingUpdate);
            return of(setThresholdsConfigFailed());
          })
        )
      )
    )
  );

  getRenewalTargetList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getRenewalTargetList),
      switchMap(action =>
        this.renewalsService.renewalTargetList(action.propertyId).pipe(
          map(list => getRenewalTargetListSuccess({ renewalTargetList: list || [] })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingRenewalStrategies);
            return of(getRenewalTargetListFailure());
          })
        )
      )
    )
  );

  updateRenewalTarget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateRenewalTarget),
      mergeMap(action =>
        this.renewalsService.updateRenewalTarget(action.renewalTargetModels).pipe(
          map(renewalsList => updateRenewalTargetSuccess()),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingRenewalStrategy);
            return of(updateRenewalTargetFailure());
          })
        )
      )
    )
  );
}
